import { Info } from "lucide-react";

/**
 * FranceEssentials — a compact banner that surfaces the France-specific
 * signals a family cares about the moment they land on a French course:
 *
 *   • Admission track (Parcoursup vs concours BCE vs direct concours)
 *   • CVEC — the mandatory €103 student services fee every French uni
 *     charges on top of tuition. Missing this line is a common
 *     "we-forgot-a-fee" surprise for families budgeting.
 *   • Native EUR salary anchor (IPD survey) alongside the USD figure
 *     already shown in the salary block — so French families don't
 *     have to mentally re-convert their government's own numbers.
 *
 * Renders nothing when the degree isn't French, so it's safe to drop
 * anywhere in the DegreeDetail page.
 */
export default function FranceEssentials({ degree, uni }) {
  const isFrench = (uni?.country || "").toLowerCase() === "france"
    || (uni?.country_code || "").toUpperCase() === "FR";
  if (!isFrench) return null;

  const cvec = degree?.cvec_fee_eur ?? degree?.cost?.cvec_eur;
  const admissionTrack = degree?.admission_track || degree?.application_platform;
  const selectivity = degree?.admission_selectivity;
  const salaryEur = degree?.outcomes?.median_starting_salary_eur;
  const salarySrc = degree?.outcomes?.salary_source;

  // If none of the France-specific fields resolved, don't clutter the
  // page with an empty banner.
  const hasAnything = cvec || admissionTrack || salaryEur;
  if (!hasAnything) return null;

  return (
    <section
      data-testid="france-essentials"
      className="mt-8 rounded-2xl border border-[#1E3A8A]/15 bg-gradient-to-br from-[#F5F3FF] via-[#EEF2FF] to-white p-5 sm:p-6"
    >
      <div className="flex items-center gap-2">
        <span aria-hidden className="text-lg">🇫🇷</span>
        <div className="label-uppercase text-[#1E3A8A]">France essentials</div>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {admissionTrack && (
          <div data-testid="france-admission-track">
            <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">
              Admission track
            </div>
            <div className="mt-1 text-sm text-stone-900 font-medium leading-snug">
              {admissionTrack}
            </div>
            {selectivity && (
              <div className="mt-1 text-[11px] font-underground-sc tracking-wider text-[#1E3A8A]">
                {selectivity}
              </div>
            )}
          </div>
        )}
        {cvec && (
          <div data-testid="france-cvec">
            <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">
              CVEC (student services fee)
            </div>
            <div className="mt-1 text-sm text-stone-900 font-medium">
              €{cvec}
              <span className="text-stone-500 font-normal"> / year, mandatory</span>
            </div>
            <div className="mt-1 text-[11px] text-stone-500 leading-relaxed">
              Paid on top of tuition. Non-refundable, covers campus life.
            </div>
          </div>
        )}
        {salaryEur && (
          <div data-testid="france-native-salary">
            <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">
              Native EUR salary (median)
            </div>
            <div className="mt-1 text-sm text-stone-900 font-medium">
              €{Number(salaryEur).toLocaleString("fr-FR")}
              <span className="text-stone-500 font-normal"> / year</span>
            </div>
            {salarySrc && (
              <div className="mt-1 text-[11px] text-stone-500 leading-relaxed inline-flex items-start gap-1">
                <Info className="h-3 w-3 mt-0.5 shrink-0" /> {salarySrc}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
